<template>
  <div
    class="block-item"
    :class="{ 'block-item--open': isOpen, 'block-item--dragging': isDragging }"
    draggable="true"
    @dragstart="$emit('drag-start')"
    @dragover.prevent="$emit('drag-over')"
    @drop="$emit('drop')"
    @dragend="$emit('drag-end')"
  >
    <!-- Row -->
    <div class="block-row">
      <!-- Drag handle -->
      <div
        class="drag-handle"
        aria-label="Arrastrar para reordenar"
        role="button"
        tabindex="0"
      >
        <q-icon name="drag_indicator" size="16px" />
        <q-tooltip>Arrastrar para reordenar</q-tooltip>
      </div>

      <!-- Type chip -->
      <div class="type-chip">{{ blockLabel }}</div>

      <!-- Content summary -->
      <div class="content-summary">{{ contentSummary }}</div>

      <!-- Actions -->
      <div class="block-actions">
        <q-btn flat dense round size="sm" :icon="isOpen ? 'expand_less' : 'expand_more'" @click="toggleOpen">
          <q-tooltip>{{ isOpen ? 'Cerrar' : 'Editar bloque' }}</q-tooltip>
        </q-btn>
        <q-btn flat dense round size="sm" icon="arrow_upward" :disable="isFirst" @click="$emit('move-up')">
          <q-tooltip>Mover arriba</q-tooltip>
        </q-btn>
        <q-btn flat dense round size="sm" icon="arrow_downward" :disable="isLast" @click="$emit('move-down')">
          <q-tooltip>Mover abajo</q-tooltip>
        </q-btn>
        <q-btn flat dense round size="sm" icon="delete" color="negative" @click="$emit('remove')">
          <q-tooltip>Eliminar bloque</q-tooltip>
        </q-btn>
      </div>
    </div>

    <!-- Accordion: inline block form -->
    <div v-if="isOpen" class="block-form-panel">
      <component
        :is="formComponent"
        v-if="formComponent"
        :block="block"
        @update="$emit('update', $event)"
      />
      <div v-else class="no-form">
        <span>Sin campos editables para este bloque.</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue';
import type { ContentBlock } from 'src/stores/useAdminCoursesStore';

const props = defineProps<{
  block: ContentBlock;
  isFirst: boolean;
  isLast: boolean;
  isDragging?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update', block: ContentBlock): void;
  (e: 'remove'): void;
  (e: 'move-up'): void;
  (e: 'move-down'): void;
  (e: 'drag-start'): void;
  (e: 'drag-over'): void;
  (e: 'drop'): void;
  (e: 'drag-end'): void;
}>();

const isOpen = ref(false);

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

// Map block types to labels
const TYPE_LABELS: Record<string, string> = {
  heading: 'TÍTULO',
  paragraph: 'PÁRRAFO',
  list: 'LISTA',
  callout: 'AVISO',
  quote: 'CITA',
  code: 'CÓDIGO',
  image: 'IMAGEN',
  toggle: 'DESPLEGABLE',
  key_points: 'PUNTOS CLAVE',
  grid: 'GRILLA',
  comparison: 'COMPARATIVA',
  divider: 'SEPARADOR',
  quiz: 'QUIZ',
};

const blockLabel = computed(() => TYPE_LABELS[props.block.type] ?? props.block.type.toUpperCase());

// 60-char content summary from main text field
const contentSummary = computed(() => {
  const b = props.block as Record<string, unknown>;
  const text =
    (b['text'] as string) ||
    (b['markdown'] as string) ||
    (b['title'] as string) ||
    (b['code'] as string) ||
    '';
  return text.length > 60 ? text.slice(0, 60) + '...' : text;
});

// Dynamic form component per block type.
// IMPORTANTE: los async components se crean UNA sola vez (identidad estable).
// Si se crearan dentro del computed, cada cambio de props.block (p. ej. una
// tecla en un input) devolveria un componente nuevo y <component :is>
// re-montaria el form, haciendo perder el foco al input en cada pulsacion.
const FORM_COMPONENTS: Record<string, ReturnType<typeof defineAsyncComponent>> = {
  heading: defineAsyncComponent(() => import('./block-forms/HeadingForm.vue')),
  paragraph: defineAsyncComponent(() => import('./block-forms/ParagraphForm.vue')),
  list: defineAsyncComponent(() => import('./block-forms/ListForm.vue')),
  callout: defineAsyncComponent(() => import('./block-forms/CalloutForm.vue')),
  quote: defineAsyncComponent(() => import('./block-forms/QuoteForm.vue')),
  code: defineAsyncComponent(() => import('./block-forms/CodeForm.vue')),
  image: defineAsyncComponent(() => import('./block-forms/ImageForm.vue')),
  toggle: defineAsyncComponent(() => import('./block-forms/ToggleForm.vue')),
  key_points: defineAsyncComponent(() => import('./block-forms/KeyPointsForm.vue')),
  grid: defineAsyncComponent(() => import('./block-forms/GridForm.vue')),
  comparison: defineAsyncComponent(() => import('./block-forms/ComparisonForm.vue')),
  quiz: defineAsyncComponent(() => import('./block-forms/QuizForm.vue')),
};

const formComponent = computed(() => FORM_COMPONENTS[props.block.type] ?? null);
</script>

<style scoped lang="scss">
.block-item {
  border: 1px solid var(--oa-border);
  border-radius: 8px;
  background: var(--oa-bg-2);
  transition: border-color 0.15s;

  &--open {
    border-left: 3px solid var(--color-ocean-light);
    background: var(--color-seafoam);
  }

  &--dragging {
    opacity: 0.6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
}

.block-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
}

.drag-handle {
  cursor: grab;
  color: var(--oa-text-disabled);
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    color: var(--color-ocean-light);
  }
}

.type-chip {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--color-ocean-light) 10%, transparent);
  color: var(--color-ocean-light);
  white-space: nowrap;
  flex-shrink: 0;
}

.content-summary {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--oa-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.block-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.block-form-panel {
  border-top: 1px solid var(--oa-border);
}

.no-form {
  padding: 12px 16px;
  font-size: 13px;
  color: var(--oa-text-muted);
}
</style>
